import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { ToasterSeverities } from "@core/enums/toaster-severities";
import { TransactionHelper } from "@core/helpers/transaction-helper";
import { BankBatch } from "@core/models/bank-batch-model";
import { BankCollectionBatch } from "@core/models/bank-collection-batch";
import { BankTransactionMatch } from "@core/models/bank-transaction-match.model";
import { CollectionTransaction } from "@core/models/collection-transaction.model";
import { DisbursementTransactionAction } from "@core/models/disbursement-transaction-action.model";
import { ExtendedGfhAccount } from "@core/models/extended-gfh-account.model";
import { FundWithRelated } from "@core/models/fund-with-related.model";
import { PendingTransaction } from "@core/models/pending-transaction.model";
import { Search } from "@core/models/search.model";
import { Transaction } from "@core/models/transaction.model";
import { NotificationService } from "@core/services/notification.service";
import { TransactionService } from "@core/services/transaction.service";
import { Utilities } from "@core/utils/utils";
import { TableSorting } from "@custom-types/sorting";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  catchError,
  mergeMap,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from "rxjs";

import * as transactionActions from "../store/transaction.actions";

import {
  SELECT_DISBURSEMENT_LIST,
  SELECT_TRANSACTION_SEARCH_ITEMS,
} from "./transaction.selectors";

@Injectable()
export class TransactionEffects {
  loadBankBatches$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.LOAD_BANK_BATCHES),
      mergeMap((action: { selectedBatchId?: string }): Observable<Action> => {
        this.store.dispatch(
          transactionActions.SET_TRANSACTION_LOADING_STATUS({ loading: true })
        );
        return this.transactionService.getBankBatchList$().pipe(
          switchMap((bankBatchList: Array<BankBatch>): Observable<Action> => {
            this.store.dispatch(
              transactionActions.SET_TRANSACTION_LOADING_STATUS({
                loading: false,
              })
            );
            if (action.selectedBatchId) {
              this.store.dispatch(
                transactionActions.SET_TRANSACTION_SEARCH_ITEMS({
                  searchItems: null,
                })
              );
              this.store.dispatch(
                transactionActions.SET_TRANSACTIONS({
                  transactionList: TransactionHelper.getTransactionList(
                    action.selectedBatchId,
                    bankBatchList
                  ),
                })
              );
            }
            return of(transactionActions.SET_BANK_BATCHES({ bankBatchList }));
          }),
          catchError((): Observable<Action> => {
            return of(
              transactionActions.SET_TRANSACTION_LOADING_STATUS({
                loading: false,
              })
            );
          })
        );
      })
    );
  });

  loadFunds$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.LOAD_FUNDS),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(
          transactionActions.SET_TRANSACTION_LOADING_STATUS({ loading: true })
        );
        return this.transactionService.getFundList$().pipe(
          switchMap((fundList: Array<FundWithRelated>): Observable<Action> => {
            this.store.dispatch(
              transactionActions.SET_TRANSACTION_LOADING_STATUS({
                loading: false,
              })
            );
            return of(transactionActions.SET_FUNDS({ fundList: fundList }));
          }),
          catchError((): Observable<Action> => {
            return of(
              transactionActions.SET_TRANSACTION_LOADING_STATUS({
                loading: false,
              })
            );
          })
        );
      })
    );
  });

  loadTransactions$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.LOAD_TRANSACTIONS),
      mergeMap((action: { searchItems: Search }): Observable<Action> => {
        this.store.dispatch(
          transactionActions.SET_TRANSACTION_LOADING_STATUS({ loading: true })
        );
        return this.transactionService
          .getTransactionList$(action.searchItems)
          .pipe(
            switchMap(
              (transactionList: Array<Transaction>): Observable<Action> => {
                this.store.dispatch(
                  transactionActions.SET_TRANSACTION_LOADING_STATUS({
                    loading: false,
                  })
                );
                this.store.dispatch(
                  transactionActions.SET_TRANSACTION_SEARCH_ITEMS({
                    searchItems: action.searchItems,
                  })
                );
                return of(
                  transactionActions.SET_TRANSACTIONS({ transactionList })
                );
              }
            ),
            catchError((): Observable<Action> => {
              return of(
                transactionActions.SET_TRANSACTION_LOADING_STATUS({
                  loading: false,
                })
              );
            })
          );
      })
    );
  });

  uploadCustomerStatement$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.UPLOAD_CUSTOMER_STATEMENT),
      mergeMap((action: { document: File }): Observable<Action> => {
        return this.transactionService
          .uploadCustomerStatement$(action.document)
          .pipe(
            switchMap((): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Success,
                "FILE_UPLOADED_SUCCESSFULLY"
              );
              return of(transactionActions.LOAD_BANK_BATCHES({}));
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "FILE_COULD_NOT_BE_UPLOADED",
                response?.error
              );
              return of();
            })
          );
      })
    );
  });

  revertTransaction$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.REVERT_TRANSACTION),
      withLatestFrom(this.store.select(SELECT_TRANSACTION_SEARCH_ITEMS)),
      mergeMap(
        ([action, searchItems]: [
          { id: string; selectedBatchId: string },
          Search | null,
        ]): Observable<Action> => {
          this.store.dispatch(
            transactionActions.SET_TRANSACTION_LOADING_STATUS({ loading: true })
          );
          return this.transactionService.revertTransaction$(action.id).pipe(
            switchMap((): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Success,
                "DATA_REVERTED_SUCCESSFULLY"
              );
              if (searchItems) {
                this.store.dispatch(
                  transactionActions.LOAD_TRANSACTIONS({ searchItems })
                );
              }
              return of(
                transactionActions.LOAD_BANK_BATCHES({
                  selectedBatchId: action.selectedBatchId,
                })
              );
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "DATA_COULD_NOT_BE_REVERTED",
                response?.error
              );
              return of(
                transactionActions.SET_TRANSACTION_LOADING_STATUS({
                  loading: false,
                })
              );
            })
          );
        }
      )
    );
  });

  suppressTransaction$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.SUPPRESS_TRANSACTION),
      withLatestFrom(this.store.select(SELECT_TRANSACTION_SEARCH_ITEMS)),
      mergeMap(
        ([action, searchItems]: [
          {
            id: string;
            suppressed: boolean;
            version: string;
            selectedBatchId: string;
          },
          Search | null,
        ]): Observable<Action> => {
          this.store.dispatch(
            transactionActions.SET_TRANSACTION_LOADING_STATUS({ loading: true })
          );
          return this.transactionService
            .suppressTransaction$(action.id, action.suppressed, action.version)
            .pipe(
              switchMap((): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_UPDATED_SUCCESSFULLY"
                );
                if (searchItems) {
                  this.store.dispatch(
                    transactionActions.LOAD_TRANSACTIONS({ searchItems })
                  );
                }
                return of(
                  transactionActions.LOAD_BANK_BATCHES({
                    selectedBatchId: action.selectedBatchId,
                  })
                );
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_COULD_NOT_BE_UPDATED",
                  response?.error
                );
                return of(
                  transactionActions.SET_TRANSACTION_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadGfhAccounts$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.LOAD_GFH_ACCOUNTS),
      mergeMap(
        (action: {
          transaction?: Transaction;
          searchTerm?: string;
        }): Observable<Action> => {
          this.store.dispatch(
            transactionActions.SET_TRANSACTION_LOADING_STATUS({ loading: true })
          );
          let gfhAccountIds: Array<string> = [];
          if (!action.searchTerm && action.transaction) {
            gfhAccountIds = TransactionHelper.getGfhAccountIds(
              action.transaction
            );
            if (!gfhAccountIds.length) {
              this.store.dispatch(
                transactionActions.SET_GFH_ACCOUNTS({ gfhAccountList: [] })
              );
              return of(
                transactionActions.SET_TRANSACTION_LOADING_STATUS({
                  loading: false,
                })
              );
            }
          }
          return this.transactionService
            .getGfhAccountList$(gfhAccountIds, action.searchTerm)
            .pipe(
              switchMap(
                (
                  gfhAccountList: Array<ExtendedGfhAccount>
                ): Observable<Action> => {
                  this.store.dispatch(
                    transactionActions.SET_TRANSACTION_LOADING_STATUS({
                      loading: false,
                    })
                  );
                  return of(
                    transactionActions.SET_GFH_ACCOUNTS({ gfhAccountList })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  transactionActions.SET_TRANSACTION_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  updateTransactionMatch$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.UPDATE_TRANSACTION_MATCH),
      withLatestFrom(this.store.select(SELECT_TRANSACTION_SEARCH_ITEMS)),
      mergeMap(
        ([action, searchItems]: [
          {
            bankTransactionMatch: BankTransactionMatch;
            matchId: string;
            selectedBatchId: string;
          },
          Search | null,
        ]): Observable<Action> => {
          return this.transactionService
            .saveTransactionMatch$(action.bankTransactionMatch, action.matchId)
            .pipe(
              switchMap((): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_SAVED_SUCCESSFULLY"
                );
                if (searchItems) {
                  this.store.dispatch(
                    transactionActions.LOAD_TRANSACTIONS({ searchItems })
                  );
                }
                return of(
                  transactionActions.LOAD_BANK_BATCHES({
                    selectedBatchId: action.selectedBatchId,
                  })
                );
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_COULD_NOT_BE_SAVED",
                  response?.error
                );
                return of();
              })
            );
        }
      )
    );
  });

  loadBankCollectionBatches$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.LOAD_BANK_COLLECTION_BATCHES),
      mergeMap((action: { sorting?: TableSorting }): Observable<Action> => {
        this.store.dispatch(
          transactionActions.SET_COLLECTION_LOADING_STATUS({ loading: true })
        );
        return this.transactionService
          .getBankCollectionBatchList$(action.sorting)
          .pipe(
            switchMap(
              (
                bankCollectionBatchList: Array<BankCollectionBatch>
              ): Observable<Action> => {
                this.store.dispatch(
                  transactionActions.SET_COLLECTION_LOADING_STATUS({
                    loading: false,
                  })
                );
                return of(
                  transactionActions.SET_BANK_COLLECTION_BATCHES({
                    bankCollectionBatchList,
                  })
                );
              }
            ),
            catchError((): Observable<Action> => {
              return of(
                transactionActions.SET_COLLECTION_LOADING_STATUS({
                  loading: false,
                })
              );
            })
          );
      })
    );
  });

  downloadCollection$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.DOWNLOAD_COLLECTION),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.transactionService.getCollection$(action.id).pipe(
          switchMap((file: HttpResponse<ArrayBuffer>): Observable<Action> => {
            const blob = TransactionHelper.getBlobInformation(file);
            Utilities.downloadAttachment(blob.data, blob.fileName);
            this.notificationService.show(
              ToasterSeverities.Success,
              "FILE_DOWNLOADED_SUCCESSFULLY"
            );
            return of();
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "DATA_COULD_NOT_BE_DOWNLOADED",
              response?.error
            );
            return of();
          })
        );
      })
    );
  });

  exportPeriodicCollection$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.EXPORT_PERIODIC_COLLECTION),
      mergeMap((): Observable<Action> => {
        return this.transactionService.exportPeriodicCollection$().pipe(
          switchMap((): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Success,
              "PERIODIC_COLLECTION_BACKGROUND"
            );
            return of();
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "ERROR_BACKGROUND",
              response?.error
            );
            return of();
          })
        );
      })
    );
  });

  loadBankRetryCollectionBatches$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.LOAD_BANK_RETRY_COLLECTION_BATCHES),
      mergeMap((action: { sorting?: TableSorting }): Observable<Action> => {
        this.store.dispatch(
          transactionActions.SET_RETRY_COLLECTION_LOADING_STATUS({
            loading: true,
          })
        );
        return this.transactionService
          .getBankRetryCollectionBatchList$(action.sorting)
          .pipe(
            switchMap(
              (
                bankCollectionBatchList: Array<BankCollectionBatch>
              ): Observable<Action> => {
                this.store.dispatch(
                  transactionActions.SET_RETRY_COLLECTION_LOADING_STATUS({
                    loading: false,
                  })
                );
                return of(
                  transactionActions.SET_BANK_RETRY_COLLECTION_BATCHES({
                    bankCollectionBatchList,
                  })
                );
              }
            ),
            catchError((): Observable<Action> => {
              return of(
                transactionActions.SET_RETRY_COLLECTION_LOADING_STATUS({
                  loading: false,
                })
              );
            })
          );
      })
    );
  });

  loadBankFailedCollectionBatches$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.LOAD_BANK_FAILED_COLLECTION_BATCHES),
      mergeMap((action: { sorting?: TableSorting }): Observable<Action> => {
        this.store.dispatch(
          transactionActions.SET_FAILED_COLLECTION_LOADING_STATUS({
            loading: true,
          })
        );
        return this.transactionService
          .getBankFailedCollectionBatchList$(action.sorting)
          .pipe(
            switchMap(
              (
                bankCollectionBatchList: Array<CollectionTransaction>
              ): Observable<Action> => {
                this.store.dispatch(
                  transactionActions.SET_FAILED_COLLECTION_LOADING_STATUS({
                    loading: false,
                  })
                );
                return of(
                  transactionActions.SET_BANK_FAILED_COLLECTION_BATCHES({
                    bankCollectionBatchList,
                  })
                );
              }
            ),
            catchError((): Observable<Action> => {
              return of(
                transactionActions.SET_FAILED_COLLECTION_LOADING_STATUS({
                  loading: false,
                })
              );
            })
          );
      })
    );
  });

  exportFailedCollection$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.EXPORT_FAILED_COLLECTION),
      mergeMap(
        (action: { collectionsToRetry: Array<string> }): Observable<Action> => {
          return this.transactionService
            .exportFailedCollection$(action.collectionsToRetry)
            .pipe(
              switchMap((): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "RETRY_COLLECTION_BACKGROUND"
                );
                return of(
                  transactionActions.LOAD_BANK_FAILED_COLLECTION_BATCHES({})
                );
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "ERROR_BACKGROUND",
                  response?.error
                );
                return of();
              })
            );
        }
      )
    );
  });

  suppressCollectionTransaction$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.SUPPRESS_COLLECTION_TRANSACTION),
      mergeMap(
        (action: { id: string; version: string }): Observable<Action> => {
          this.store.dispatch(
            transactionActions.SET_FAILED_COLLECTION_LOADING_STATUS({
              loading: true,
            })
          );
          return this.transactionService
            .suppressCollectionTransaction$(action)
            .pipe(
              switchMap((): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DATA_UPDATED_SUCCESSFULLY"
                );
                return of(
                  transactionActions.LOAD_BANK_FAILED_COLLECTION_BATCHES({})
                );
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DATA_COULD_NOT_BE_UPDATED",
                  response?.error
                );
                return of(
                  transactionActions.SET_FAILED_COLLECTION_LOADING_STATUS({
                    loading: true,
                  })
                );
              })
            );
        }
      )
    );
  });

  makeCollectionsDefinitive$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.MAKE_COLLECTIONS_DEFINITIVE),
      mergeMap((): Observable<Action> => {
        return this.transactionService.makeCollectionsDefinitive$().pipe(
          switchMap((): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Success,
              "DEFINITIVE_COLLECTION_BACKGROUND"
            );
            return of();
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "ERROR_BACKGROUND",
              response?.error
            );
            return of();
          })
        );
      })
    );
  });

  loadDisbursements$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.LOAD_DISBURSEMENTS),
      mergeMap((action: { sorting?: TableSorting }): Observable<Action> => {
        this.store.dispatch(
          transactionActions.SET_DISBURSEMENT_LOADING_STATUS({ loading: true })
        );
        return this.transactionService
          .getDisbursementList$(action.sorting)
          .pipe(
            switchMap(
              (
                disbursementList: Array<PendingTransaction>
              ): Observable<Action> => {
                this.store.dispatch(
                  transactionActions.SET_DISBURSEMENTS({ disbursementList })
                );
                return of(
                  transactionActions.SET_DISBURSEMENT_LOADING_STATUS({
                    loading: false,
                  })
                );
              }
            ),
            catchError((): Observable<Action> => {
              return of(
                transactionActions.SET_DISBURSEMENT_LOADING_STATUS({
                  loading: false,
                })
              );
            })
          );
      })
    );
  });

  createDisbursementBatch$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.CREATE_DISBURSEMENT_BATCH),
      withLatestFrom(this.store.select(SELECT_DISBURSEMENT_LIST)),
      mergeMap(
        ([action, disbursementList]: [
          {
            disbursementTransactionActionList: Array<DisbursementTransactionAction>;
            selectedDisbursementList: Array<PendingTransaction>;
          },
          Array<PendingTransaction>,
        ]): Observable<Action> => {
          this.store.dispatch(
            transactionActions.SET_DISBURSEMENT_LOADING_STATUS({
              loading: true,
            })
          );
          return this.transactionService
            .createDisbursementBatch$(action.disbursementTransactionActionList)
            .pipe(
              switchMap((): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DISBURSEMENT_BACKGROUND"
                );
                this.store.dispatch(
                  transactionActions.SET_DISBURSEMENT_LOADING_STATUS({
                    loading: false,
                  })
                );
                return of(
                  transactionActions.SET_DISBURSEMENTS({
                    disbursementList:
                      TransactionHelper.getUnselectedDisbursementList(
                        disbursementList,
                        action.selectedDisbursementList
                      ),
                  })
                );
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "ERROR_BACKGROUND",
                  response?.error
                );
                return of(
                  transactionActions.SET_DISBURSEMENT_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  createDisbursementReport$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.CREATE_DISBURSEMENT_REPORT),
      mergeMap((): Observable<Action> => {
        return this.transactionService.createDisbursementReport$().pipe(
          switchMap((): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Success,
              "DISBURSEMENT_REPORT_BACKGROUND"
            );
            return of();
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "ERROR_BACKGROUND",
              response?.error
            );
            return of();
          })
        );
      })
    );
  });

  processDisbursement$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(transactionActions.PROCESS_DISBURSEMENT),
      mergeMap(
        (action: {
          disbursementTransactionAction: DisbursementTransactionAction;
        }): Observable<Action> => {
          this.store.dispatch(
            transactionActions.SET_DISBURSEMENT_LOADING_STATUS({
              loading: true,
            })
          );
          return this.transactionService
            .processDisbursement$(action.disbursementTransactionAction)
            .pipe(
              switchMap((): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "DISBURSEMENT_ACTION"
                );
                return of(transactionActions.LOAD_DISBURSEMENTS({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "DISBURSEMENT_ACTION_ERROR",
                  response?.error
                );
                return of(
                  transactionActions.SET_DISBURSEMENT_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  constructor(
    private actions$: Actions,
    private transactionService: TransactionService,
    private store: Store,
    private notificationService: NotificationService
  ) {}
}
